<script setup lang="ts">
import SimpleDropdown from "~/components/Common/Dropdowns/SimpleDropdown.vue";
import { SessionItemsFormats } from "~/utils/options/session-items.options";
import DateInput from "~/components/Common/Inputs/DateInput.vue";
import { useSessionsStore } from "~/store/sessions/sessions-store";
import { storeToRefs } from "pinia";
import AreaInput from "~/components/Common/Inputs/AreaInput.vue";
import { startOfDay, isSameMinute, intervalToDuration } from "date-fns";
import { handleValueInput } from "~/utils/helpers/handleValueInput";
import TimePicker from "~/components/Common/Inputs/TimePicker.vue";
import { useSessions } from "~/composables/sessions/useSessions";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { useAuthStore } from "~/store/auth/auth.store";
import { TimezoneDisplayModeEnum } from "~/utils/enums/timezones.enums";
import { useReaderDateTime } from "~/composables/timezones/useReaderDateTime";

const sessionsStore = useSessionsStore();
const { sessionItemFormatMap } = useSessions();
const handleValue = handleValueInput();
const authStore = useAuthStore();

const emit = defineEmits(["loaded", "hide"]);

const { trainingPlanSessionIndividual, sessions, showUpdateSessionModal } = storeToRefs(sessionsStore);
const confirmId: Ref<string | null> = ref(null);
const durationDays = ref<number>(0);

const warningMaxAttendees = ref(false);

onMounted(async () => {
  if (trainingPlanSessionIndividual.value) {
    confirmId.value = trainingPlanSessionIndividual.value.bookingId;

    // Capture initial booking data before any modifications
    if (trainingPlanSessionIndividual.value.sessionId) {
      initialBookingSessionId.value = trainingPlanSessionIndividual.value.sessionId;
      initialBookingData.value = {
        sessionId: trainingPlanSessionIndividual.value.sessionId,
        trainingId: trainingPlanSessionIndividual.value.trainingId,
        location: trainingPlanSessionIndividual.value.location,
        format: trainingPlanSessionIndividual.value.format,
        startDate: trainingPlanSessionIndividual.value.startDate,
        endDate: trainingPlanSessionIndividual.value.endDate,
        designation: trainingPlanSessionIndividual.value.designation,
        startTime: trainingPlanSessionIndividual.value.startTime,
        endTime: trainingPlanSessionIndividual.value.endTime,
        trainingDuration: trainingPlanSessionIndividual.value.trainingDuration,
      };
    }

    // swap for trainingid
    if (trainingPlanSessionIndividual.value.trainingId) {
      await sessionsStore.fetchSessionsByTrainingId({
        trainingId: trainingPlanSessionIndividual.value.trainingId,
        withProject: "false",
        inProgress: "true",
      });
    }

    if (trainingPlanSessionIndividual.value?.startDate && trainingPlanSessionIndividual.value?.endDate) {
      durationDays.value =
        intervalToDuration({
          start: new Date(trainingPlanSessionIndividual.value.startDate),
          end: new Date(trainingPlanSessionIndividual.value?.endDate),
        })?.days ?? 0;
    } else {
      durationDays.value = 0;
    }

    //TODO : debug to see why the value switch null after the first if
    if (trainingPlanSessionIndividual.value) {
      trainingPlanSessionIndividual.value.format =
        SessionItemsFormats.find((x) => x.name === trainingPlanSessionIndividual?.value?.format)?.name ?? null;
    }
  }
  emit("loaded");
});

const props = defineProps({
  disable: {
    type: Boolean,
    default: false,
  },
  /** READER from « Choisir une date » in Mon activité; the admin status modal keeps STEP. */
  timezoneMode: {
    type: String as PropType<TimezoneDisplayModeEnum>,
    default: TimezoneDisplayModeEnum.STEP,
  },
});

const sessionId = computed({
  get() {
    if (trainingPlanSessionIndividual.value?.sessionId && listSession.value.length) {
      const session = listSession.value.find((ls) => ls.sessionId === trainingPlanSessionIndividual.value?.sessionId);
      if (session?.nbAttendees && session?.maxNbAttendees && session.nbAttendees > session?.maxNbAttendees) {
        warningMaxAttendees.value = true;
      }
      return session;
    }
    return trainingPlanSessionIndividual.value;
  },
  set(newValue: any) {
    if (newValue.maxNbAttendees && newValue.nbAttendees >= newValue.maxNbAttendees) {
      warningMaxAttendees.value = true;
    } else {
      warningMaxAttendees.value = false;
    }
    if (trainingPlanSessionIndividual.value) {
      trainingPlanSessionIndividual.value.sessionId = newValue.sessionId;
      trainingPlanSessionIndividual.value.format =
        SessionItemsFormats.find((x) => x.name === newValue.format)?.name ?? null;
      trainingPlanSessionIndividual.value.startDate = newValue.startDate;

      trainingPlanSessionIndividual.value.endDate =
        newValue.startDate && newValue.endDate && newValue.startDate > newValue.endDate
          ? newValue.startDate
          : newValue.endDate;
      trainingPlanSessionIndividual.value.location = newValue.trainingAddress;
    }
  },
});

const { readerDateTime, displayDay } = useReaderDateTime(() => props.timezoneMode);

/** Only a listed session carries its steps' zones; the booking's own reads as Europe/Paris. */
const sessionZones = computed(() => {
  const session = sessionId.value;
  return session && "startTimezone" in session
    ? { start: session.startTimezone ?? null, end: session.endTimezone ?? null }
    : { start: null, end: null };
});

/** The date and time fields are disabled here: only what they show changes, never what is saved. */
const readerStart = computed(() =>
  readerDateTime(sessionId.value?.startDate, sessionId.value?.startTime, sessionZones.value.start),
);
const readerEnd = computed(() =>
  readerDateTime(sessionId.value?.endDate, sessionId.value?.endTime, sessionZones.value.end),
);

// SelectTimePicker takes `undefined` where the session holds `null`.
const startTimeValue = computed({
  get(): string | undefined {
    return readerStart.value?.time ?? sessionId.value?.startTime ?? undefined;
  },
  set(value: string | undefined) {
    if (sessionId.value) {
      sessionId.value.startTime = value ?? null;
    }
  },
});

const endTimeValue = computed({
  get(): string | undefined {
    return readerEnd.value?.time ?? sessionId.value?.endTime ?? undefined;
  },
  set(value: string | undefined) {
    if (sessionId.value) {
      sessionId.value.endTime = value ?? null;
    }
  },
});

// TODO : remove this interface when the backend is updated
interface CamelCaseSession {
  sessionId?: string;
  trainingId?: string;
  trainingAddress?: string | null;
  priceExclTax?: number;
  searchFields?: string;
  sessionType?: string;
  modality?: string;
  format?: string | null;
  trainingTitle?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  startTimezone?: string | null;
  endTimezone?: string | null;
  maxNbAttendees?: number | null;
  nbRemainingSlots?: number | null;
  designation?: string | null;
  sessionDraftId?: string | null;
  nbAttendees?: number | null;
  isCheck?: boolean;
  overbookingNotAllowed?: boolean;
  trainingDuration?: number | null;
}

// Store the initial sessionId to check only once
const initialBookingSessionId = ref<string | null>(null);

const listSession = computed(() => {
  const list: CamelCaseSession[] = [
    ...(sessions.value?.length ? sessions.value.map((session) => ({ ...session })) : []),
  ];

  // Add the initial booking session only if it's not already in the list
  // This check is done only once with the initial booking sessionId
  if (
    list.length &&
    initialBookingSessionId.value &&
    !list.some((el) => el.sessionId === initialBookingSessionId.value)
  ) {
    addInitialBookingSessionToList(list);
  }

  return list;
});

// Store initial data to avoid reactivity changes
const initialBookingData = ref<any>(null);

function addInitialBookingSessionToList(sessions: CamelCaseSession[]) {
  if (initialBookingData.value && initialBookingData.value.startDate) {
    sessions.push({
      sessionId: initialBookingData.value.sessionId!,
      trainingId: initialBookingData.value.trainingId!,
      trainingAddress: initialBookingData.value.location,
      format: initialBookingData.value.format,
      startDate: initialBookingData.value.startDate,
      endDate: initialBookingData.value.endDate,
      designation: initialBookingData.value.designation,
      startTime: initialBookingData.value.startTime,
      endTime: initialBookingData.value.endTime,
      priceExclTax: 0,
      searchFields: "",
      sessionType: "",
      modality: "",
      trainingTitle: "",
      maxNbAttendees: 0,
      nbRemainingSlots: 0,
      sessionDraftId: "",
      nbAttendees: 0,
      trainingDuration: initialBookingData.value.trainingDuration,
    });
  }
}

function noSession() {
  if (!trainingPlanSessionIndividual.value?.startDate && !listSession.value.length) {
    return true;
  }
  return false;
}

function innerStep() {
  if (trainingPlanSessionIndividual.value && noSession()) {
    const today: Date = new Date();
    trainingPlanSessionIndividual.value.sessionId = "";
    trainingPlanSessionIndividual.value.startDate = new Date().toISOString();
    trainingPlanSessionIndividual.value.endDate = new Date(
      today.setDate(today.getDate() + durationDays.value),
    ).toISOString();
    return true;
  } else if (noSession()) {
    return false;
  }
  return true;
}

const dpPlaceholder = computed(() => {
  if (noSession()) {
    return "Aucune session à venir";
  }
  return "Nouvelle session à la demande";
});

const hasSelectTimePickerError = computed(() => {
  const startDateModel =
    trainingPlanSessionIndividual.value &&
    trainingPlanSessionIndividual.value?.startDate &&
    new Date(trainingPlanSessionIndividual.value?.startDate);
  const endDateModel =
    trainingPlanSessionIndividual.value &&
    trainingPlanSessionIndividual.value?.endDate &&
    new Date(trainingPlanSessionIndividual.value?.endDate);

  return !!(
    startDateModel &&
    endDateModel &&
    (startDateModel > endDateModel ||
      (isSameMinute(startDateModel, startOfDay(startDateModel)) &&
        !isSameMinute(endDateModel, startOfDay(endDateModel))) ||
      (!isSameMinute(startDateModel, startOfDay(startDateModel)) &&
        isSameMinute(endDateModel, startOfDay(endDateModel))))
  );
});

const location = computed(() => {
  const ts = trainingPlanSessionIndividual.value?.timeslots?.[0];
  if (ts?.selectedLocation) {
    return [
      ts.selectedLocation.locationName,
      ts.selectedLocation.locationManualAddress,
      ts.selectedRoom?.roomName,
      ts.selectedRoom?.roomAccess,
    ]
      .filter(Boolean)
      .join(" - ");
  } else if (ts?.location) {
    return ts.location;
  }
});
</script>

<template>
  <div v-if="trainingPlanSessionIndividual && innerStep()">
    <div class="invite_step_date">
      <h3 class="heading_3_demi">
        {{ !showUpdateSessionModal ? "Choisissez votre date de formation" : "Changer de session" }}
      </h3>
      <span v-if="sessionsStore.errorConfirmProject" class="body_regular text-primary-red-color px-2"
        >Veuillez sélectionner une session où il reste au moins une place disponible ou contacter votre équipe RH.</span
      >
      <div>
        <h4 class="text-secondary-color pb-[4px]">Choix de session</h4>
        <SimpleDropdown
          v-model="sessionId"
          :options="listSession"
          :placeholder="dpPlaceholder"
          :disabled="noSession() || !listSession.length || disable"
          label="sessionId"
          track-by="sessionId"
          :delete="false"
        >
          <template #singleLabel="{ option }">
            <div class="flex justify-between w-full">
              <div class="flex truncate w-full">
                <span
                  >du {{ displayDay(option.startDate, option.startTime, option.startTimezone) }} au
                  {{ displayDay(option.endDate, option.endTime, option.endTimezone) }}
                </span>
                <div v-if="option.designation">
                  <span class="mx-1">-</span>
                  <span class="body_med">{{ option.designation }}</span>
                </div>
              </div>

              <div>
                <div class="flex items-center whitespace-nowrap gap-1">
                  <SvgIcon name="student" class="w-4 h-4 text-secondary-color" />
                  <span v-if="option.maxNbAttendees" class="text-secondary-color"
                    >{{ option.nbAttendees }} / {{ option.maxNbAttendees }}</span
                  >
                  <span v-else class="text-secondary-color">{{ option.nbAttendees }}</span>
                </div>
              </div>
            </div>
          </template>
          <template #option="{ option }">
            <div class="flex">
              <span
                >du
                {{ option.startDate ? displayDay(option.startDate, option.startTime, option.startTimezone) : "" }}
                au
                {{ option.endDate ? displayDay(option.endDate, option.endTime, option.endTimezone) : "" }}
              </span>
              <div v-if="option.designation">
                <span class="mx-1">-</span>
                <span class="body_med">{{ option.designation }}</span>
              </div>
            </div>
          </template>
          <template #add>Ajouter une session</template>
        </SimpleDropdown>
        <div
          v-if="warningMaxAttendees && !authStore.isOnlyManager && !authStore.isEmployee"
          class="text-secondary-color flex items-center gap-2 mt-1"
        >
          <SvgIcon name="info" class="w-4 h-4"></SvgIcon>
          <span>Le nombre de places maximal a été atteint sur cette session.</span>
        </div>
        <h4 v-if="noSession()" class="text-primary-red-color">
          Il n’y a pas de date de session à venir pour cette formation. Contactez votre équipe RH.
        </h4>
      </div>
      <div v-if="sessionId" class="dates__collective_form">
        <div class="dates_inputs">
          <div class="w-full">
            <div class="flex justify-between items-center">
              <h5 class="titles">Date de début</h5>
            </div>
            <div class="flex w-full gap-[8px]">
              <DateInput
                :model-value="readerStart?.isoDate ?? trainingPlanSessionIndividual.startDate"
                :disabled="!!sessionId"
                :min-date="new Date()"
                placeholder="Choisir"
                @update:model-value="
                  (value) => trainingPlanSessionIndividual && (trainingPlanSessionIndividual.startDate = value)
                "
              ></DateInput>
              <div>
                <TimePicker
                  v-model="startTimeValue"
                  :error="hasSelectTimePickerError"
                  :disabled="!!sessionId || !trainingPlanSessionIndividual.startDate"
                />
              </div>
            </div>
          </div>

          <div class="w-full">
            <div class="flex justify-between items-center">
              <h5 class="flex w-full gap-[8px]">Date de fin</h5>
            </div>
            <div class="flex w-full gap-[8px]">
              <DateInput
                :model-value="readerEnd?.isoDate ?? trainingPlanSessionIndividual.endDate"
                :disabled="!!sessionId"
                :min-date="trainingPlanSessionIndividual.startDate"
                placeholder="Choisir"
                @update:model-value="
                  (value) => trainingPlanSessionIndividual && (trainingPlanSessionIndividual.endDate = value)
                "
              ></DateInput>
              <div>
                <TimePicker
                  v-model="endTimeValue"
                  :error="hasSelectTimePickerError"
                  :disabled="!!sessionId || !trainingPlanSessionIndividual.endDate"
                  :min-date="
                    trainingPlanSessionIndividual.startDate &&
                    trainingPlanSessionIndividual.endDate &&
                    new Date(trainingPlanSessionIndividual.startDate).getDate() ===
                      new Date(trainingPlanSessionIndividual.endDate).getDate()
                      ? new Date(trainingPlanSessionIndividual.startDate)
                      : undefined
                  "
                />
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="
            trainingPlanSessionIndividual.startDate &&
            trainingPlanSessionIndividual.endDate &&
            handleValue.checkDates(
              new Date(trainingPlanSessionIndividual.startDate),
              new Date(trainingPlanSessionIndividual.endDate),
            )
          "
        >
          <span
            class="flex flex-row-reverse text-primary-red-color mt-1"
            v-text="
              trainingPlanSessionIndividual.startDate &&
              trainingPlanSessionIndividual.endDate &&
              handleValue.checkDates(
                new Date(trainingPlanSessionIndividual.startDate),
                new Date(trainingPlanSessionIndividual.endDate),
              )
            "
          ></span>
        </div>
        <div class="format_duration_inputs">
          <div class="w-full">
            <h5 class="titles">Format</h5>
            <input
              :disabled="true"
              placeholder="À définir"
              class="input__default body_regular w-full text-end"
              :value="sessionItemFormatMap(trainingPlanSessionIndividual.format as string)?.value"
              type="text"
            />
          </div>
          <div class="w-full">
            <h5 class="titles">Durée</h5>
            <div v-if="sessionId" class="flex items-center gap-[8px]">
              <input
                v-model="sessionId.trainingDuration"
                :disabled="true"
                placeholder="À définir"
                class="input__default w-full text-end heading_4_regular"
                type="number"
              />
              <p v-if="true" class="text-tertiary-color">heures</p>
            </div>
          </div>
        </div>
        <div class="w-full">
          <h5 class="titles">Lieu</h5>
          <input
            v-model="location"
            :disabled="true"
            placeholder="À définir"
            class="input__default w-full heading_4_regular"
            type="text"
          />
        </div>
        <div class="w-full">
          <h5 class="titles">Indication</h5>
          <input
            v-model="trainingPlanSessionIndividual.designation"
            :disabled="true"
            placeholder="À définir"
            class="input__default w-full heading_4_regular"
            type="text"
          />
        </div>
      </div>

      <div v-if="!showUpdateSessionModal">
        <h5 class="titles">Message de votre équipe RH</h5>
        <AreaInput
          class="textarea__input resize-none overflow-hidden text-area"
          disabled
          :value="trainingPlanSessionIndividual?.comments"
        ></AreaInput>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "assets/scss/texts";

.invite_step_date {
  @apply flex flex-col gap-[16px] mx-[64px] py-[52px];
}

.titles {
  @apply text-secondary-color pb-[4px];
}

.dates__collective_form {
  @extend .body_regular;

  @apply text-secondary-color flex flex-col gap-[16px];

  input {
    @extend .body_regular;
  }
}

.invite_step_date__footer_container {
  @apply flex items-center w-full flex-row justify-between gap-[16px];
  height: 68px;
  min-height: 68px;
  border-top: 1px solid var(--grey-lightest);
}

.dates_inputs {
  @apply flex gap-[16px];
}

.format_duration_inputs {
  @apply flex gap-[16px];
}

@media (max-width: 768px) {
  .dates_inputs {
    @apply flex-wrap gap-[8px];
  }

  .invite_step_date {
    @apply mx-[12px];
  }
}
</style>
